import React, { useState, useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Float, Environment } from '@react-three/drei';
import Book3D from '../components/3d/Book3D';
import FloatingParticles from '../components/3d/FloatingParticles';
import { getBooks } from '../services/api';
import './Books.css';

interface Book {
  id: number;
  title: string;
  description: string;
  cover_image: string;
  author: string;
  year: number;
  genre: string;
}

const Books: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBooks()
      .then((res) => setBooks(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const bookColors = ['#c9a84c', '#e94560', '#4ecdc4', '#a855f7', '#f97316'];

  return (
    <div className="books-page">
      <section className="books-hero">
        <div className="books-3d-scene">
          <Canvas camera={{ position: [0, 0, 8], fov: 45 }}>
            <color attach="background" args={['#0a0a0f']} />
            <ambientLight intensity={0.3} />
            <directionalLight position={[5, 5, 5]} intensity={0.7} />
            <pointLight position={[-3, 2, 3]} intensity={0.4} color="#c9a84c" />
            <Suspense fallback={null}>
              {books.map((book, i) => (
                <Float key={book.id} speed={1 + i * 0.3} rotationIntensity={0.2} floatIntensity={0.4}>
                  <Book3D
                    title={book.title}
                    author={book.author}
                    color={bookColors[i % bookColors.length]}
                    position={[(i - Math.floor(books.length / 2)) * 3, 0, 0]}
                    rotation={[0.1, -0.2 + i * 0.1, 0]}
                    scale={0.8}
                    hoverable={true}
                  />
                </Float>
              ))}
              <FloatingParticles count={100} color="#c9a84c" spread={15} />
              <Environment preset="night" />
            </Suspense>
            <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.2} />
          </Canvas>
        </div>
        <div className="books-hero-overlay" />
        <motion.div
          className="books-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="page-badge">Library</span>
          <h1 className="section-title" style={{ fontSize: '3rem', color: 'white' }}>
            Book Collection
          </h1>
          <p className="section-subtitle" style={{ marginBottom: 0 }}>
            Explore stories that challenge, inspire, and transform
          </p>
        </motion.div>
      </section>

      <section className="books-list section">
        <div className="container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner" />
            </div>
          ) : books.length === 0 ? (
            <div className="empty-state">
              <p>No books available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="books-grid">
              {books.map((book, index) => (
                <motion.div
                  key={book.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/books/${book.id}`} className="book-card">
                    <div className="book-card-cover" style={{ background: `linear-gradient(135deg, ${bookColors[index % bookColors.length]}, ${bookColors[(index + 1) % bookColors.length]})` }}>
                      <span className="book-card-infinity">&#8734;</span>
                      <h3 className="book-card-title">{book.title}</h3>
                      <p className="book-card-author">{book.author}</p>
                    </div>
                    <div className="book-card-info">
                      <h3>{book.title}</h3>
                      <p className="book-card-desc">{book.description}</p>
                      <div className="book-card-meta">
                        <span className="book-genre">{book.genre}</span>
                        <span className="book-year">{book.year}</span>
                      </div>
                      <span className="book-read-link">Read Now &rarr;</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Books;
